import { useState, useCallback, useEffect } from 'react';

export const useCommit = <T>(
  parentProp: T,
  setParentProp: (value: T) => void,
  onCommit?: () => void,
  /** Will run setParentProp on every change */
  propagate?: boolean
) => {
  const [localState, localSetter] = useState(parentProp);

  const localSetterWithParent = useCallback(
    (value: T) => {
      localSetter(value);

      if (propagate) {
        setParentProp(value);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localSetter]
  );

  const cancel = useCallback(() => {
    localSetterWithParent(parentProp);
  }, [localSetterWithParent, parentProp]);

  const commit = useCallback(
    (value?: React.SetStateAction<T>) => {
      if (onCommit) {
        onCommit();
      }
      if (value === undefined) {
        setParentProp(localState); //normala funktionen, sätt värdet från localState
      } else if (typeof value === 'function') {
        setParentProp((value as (prevState: T) => T)(localState)); //sätt ett värde som beror på localState
      } else {
        setParentProp(value); //sätt ett specifikt värde (om man har ett event som ska sätta ett värde direkt oavsett lokalt state som t.ex. "Godkänn alla")
      }
    },
    [onCommit, setParentProp, localState]
  );

  useEffect(() => {
    localSetterWithParent(parentProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentProp]);

  return { localState, localSetter: localSetterWithParent, cancel, commit };
};
